import _ from 'lodash';
import { IAgent, RuleGroup } from '@/types';

type RuleGroupsCache = Record<string, string[]>;

/** A copy of rule checking service from HITL API */
export class RuleChecker {
  /** Rule groups cache can be initialized only once */
  public static cache: RuleGroupsCache = {};

  /** Check if agent can interact with an item with provided tags */
  public static checkAgentRules(args: {
    agent: IAgent;
    tags: string[];
    ruleGroups: RuleGroup[];
  }) {
    const { agent, tags, ruleGroups } = args;

    const cache = this.getCompactRuleGroups(ruleGroups);
    const rulesInGroups = this.getRulesInGroups({
      ruleGroupIds: agent.ruleGroupIds,
      rulesGroupsCache: cache,
    });

    return this.checkRules({ rulesInGroups, tags, agentId: agent.id });
  }

  protected static getCompactRuleGroups(ruleGroups: RuleGroup[]) {
    if (!_.isEmpty(this.cache)) {
      return this.cache;
    }

    for (const ruleGroup of ruleGroups) {
      this.cache[ruleGroup.groupId] = ruleGroup.rules;
    }

    return this.cache;
  }

  /** Match and group rules for given set of IDs */
  protected static getRulesInGroups(args: {
    ruleGroupIds: string[];
    rulesGroupsCache: RuleGroupsCache;
  }) {
    const { ruleGroupIds, rulesGroupsCache } = args;
    const result = [];

    for (const id of ruleGroupIds) {
      const rules = rulesGroupsCache[id];
      if (rules && rules.length) {
        result.push(rules);
      }
    }

    return result;
  }

  /** If at least one rule group matches [AND, OR, NOT] combination of tags - return true */
  protected static checkRules(options: {
    rulesInGroups?: string[][];
    tags: string[];
    agentId?: string;
  }) {
    const tags = new Set(options.tags);
    if (options.agentId && tags.has(options.agentId)) {
      return true;
    }

    const AND_SYMBOL = '&';
    const OR_SYMBOL = '|';
    const NOT_SYMBOL = '!';

    const { rulesInGroups = [] } = options;
    for (const rules of rulesInGroups) {
      let isGroupInvalid = false;
      let groupIncludesORules = false;
      let groupIncludesARules = false;
      let tagsIncludeORule = false;

      for (const rule of rules) {
        const ruleSymbol = rule[0];
        const ruleText = rule.slice(1);

        /* If tag is not allowed or tags doesn't match required set of "AND" rules */
        const tagsIncludeNRule = ruleSymbol === NOT_SYMBOL && tags.has(ruleText);
        const tagsNotIncludeARule = ruleSymbol === AND_SYMBOL && !tags.has(ruleText);

        if (tagsIncludeNRule || tagsNotIncludeARule) {
          isGroupInvalid = true;
          break;
        }

        /* Only single match for "OR" rule is required, when such rule is present */
        if (!tagsIncludeORule && ruleSymbol === OR_SYMBOL) {
          groupIncludesORules = true;

          if (tags.has(ruleText)) {
            tagsIncludeORule = true;
          }
        }

        /* Note presence of "And" rules */
        if (ruleSymbol === AND_SYMBOL) {
          groupIncludesARules = true;
        }
      }

      if (isGroupInvalid) {
        continue;
      }

      const isGroupNotEmpty = groupIncludesORules || groupIncludesARules;
      const areORulesValid = groupIncludesORules ? tagsIncludeORule : true;
      if (isGroupNotEmpty && areORulesValid) {
        return true;
      }
    }

    return false;
  }
}

